import { Button } from "@/components/ui/baseUi/button"
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/baseUi/sheet"
import { word } from "@/modules/core/utils/i18n"
import { cn } from "@/modules/lib/utils"
import {
  type HUSidebarContextProps,
  type SidebarNavigatorParams,
  type SidebarPositionType,
} from "@modules/sidebar/sidebarTypes"
import { ArrowLeft, ArrowLeftRight, FastForwardIcon, X } from "lucide-react"
import { Activity, createContext, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useLocation } from "react-router"

export const HUSidebarContext = createContext<HUSidebarContextProps | null>(null)
const SIDEBAR_CONTEXT_DEFAULT_POSITION: SidebarPositionType = "right"
export const SIDEBAR_CONTEXT_DEFAULT_WIDTH = 576

export type NavigatorStack = {
  content: React.ReactNode
  customHeaderButtons?: React.ReactElement[]
  sidebarLeftButtonText?: string
  sidebarLeftButtonColor?: string
  sidebarLeftButtonClick?: () => Promise<boolean>
  allowSidebarReduction?: boolean
  onHide?: () => Promise<boolean>
  state?: Record<string, string>
}

export function HUSidebarProvider({ children }: { children: React.ReactNode }) {
  const [isSidebarVisible, setIsSidebarVisible] = useState<boolean>(false)
  const [sidebarPosition, setSidebarPosition] = useState<SidebarPositionType>(SIDEBAR_CONTEXT_DEFAULT_POSITION)
  const [isSidebarReduced, setIsSidebarReduced] = useState<boolean>(false)
  const [navigator, setNavigator] = useState<NavigatorStack[]>([])
  const [sidebarStackOnTop, setSidebarStackOnTop] = useState<NavigatorStack | undefined>()

  const currentContent: NavigatorStack | undefined = useMemo(
    () => (navigator.length > 0 ? navigator[navigator.length - 1] : undefined),
    [navigator],
  )
  const location = useLocation()
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const onHideSidebar = useRef<(arg?: boolean) => void | Promise<void>>(() => {})

  const cleanNavigator = useCallback(
    async (runOnHideCallback = true) => {
      let navIndex = navigator.length

      while (navIndex > 0) {
        const content = navigator[navIndex - 1]

        if (typeof content?.onHide !== "undefined" && runOnHideCallback) {
          const result = await content.onHide()

          if (result) {
            navIndex--
          } else {
            break
          }
        } else {
          navIndex--
        }

        setNavigator((previousNavigatorState) => {
          return previousNavigatorState.slice(0, navIndex)
        })
      }
      return navIndex === 0
    },
    [navigator],
  )

  useEffect(() => {
    // Defer reset to the next tick so it runs after React's commit phase for the route change. Prevents removeChild error (WEB-13J) when multiple portals (Sheet + page Popovers/Dialogs) unmount in the same commit.
    const timerId = setTimeout(() => {
      setIsSidebarVisible(false)
      document.documentElement.classList.remove("no-scrollbar")
      setNavigator([])
    }, 0)
    return () => {
      clearTimeout(timerId)
    }
  }, [location])

  useEffect(() => {
    onHideSidebar.current = async (runOnHideCallback = true) => {
      const result = await cleanNavigator(runOnHideCallback)
      if (result) {
        document.documentElement.classList.remove("no-scrollbar")
        setSidebarStackOnTop(undefined)
        setIsSidebarVisible(false)
      }
    }
  }, [navigator, cleanNavigator])

  const switchSidebarSide = useCallback(() => {
    setSidebarPosition(sidebarPosition === "left" ? "right" : "left")
  }, [sidebarPosition])

  const toggleSidebarReduced = useCallback(() => {
    setIsSidebarReduced((v) => !v)
  }, [])

  const onSidebarNavigate = async ({
    position,
    leftButtonText,
    leftButtonColor,
    leftButtonAction,
    replaceLastComponent = false,
    replaceAllComponents = false,
    allowSidebarReduction = false,
    onHide,
    content,
  }: SidebarNavigatorParams) => {
    document.documentElement.classList.add("no-scrollbar")

    setIsSidebarVisible(true)
    if (replaceAllComponents) {
      const result = await cleanNavigator()
      if (result) {
        if (position) setSidebarPosition(position)

        setNavigator([
          {
            content,
            sidebarLeftButtonText: leftButtonText,
            sidebarLeftButtonClick: leftButtonAction,
            sidebarLeftButtonColor: leftButtonColor,
            allowSidebarReduction,
            onHide,
          },
        ])
      }
      return result
    } else if (replaceLastComponent) {
      if (typeof currentContent?.onHide !== "undefined") {
        const result = await currentContent.onHide()

        if (result) {
          if (position) setSidebarPosition(position)

          setNavigator((previousNavigatorState) => {
            return [
              ...previousNavigatorState.slice(0, -1),
              {
                content,
                sidebarLeftButtonText: leftButtonText,
                sidebarLeftButtonColor: leftButtonColor,
                sidebarLeftButtonClick: leftButtonAction,
                allowSidebarReduction,
                onHide,
              },
            ]
          })
        }
        return result
      } else {
        if (position) setSidebarPosition(position)

        setNavigator((previousNavigatorState) => {
          return [
            ...previousNavigatorState.slice(0, -1),
            {
              content,
              sidebarLeftButtonText: leftButtonText,
              sidebarLeftButtonColor: leftButtonColor,
              sidebarLeftButtonClick: leftButtonAction,
              allowSidebarReduction,
              onHide,
            },
          ]
        })
        return true
      }
    } else {
      if (position) setSidebarPosition(position)

      setNavigator((previousNavigatorState) => [
        ...previousNavigatorState,
        {
          content,
          sidebarLeftButtonText: leftButtonText,
          sidebarLeftButtonColor: leftButtonColor,
          sidebarLeftButtonClick: leftButtonAction,
          allowSidebarReduction,
          onHide,
        },
      ])

      return true
    }
  }

  const onSidebarNavigateBack = async (runOnHideCallback = true) => {
    if (typeof currentContent?.onHide !== "undefined" && runOnHideCallback) {
      const result = await currentContent.onHide()

      if (result) {
        setNavigator((previousNavigatorState) => {
          const newNavigator = previousNavigatorState.slice(0, -1)
          if (newNavigator.length === 0) setIsSidebarVisible(false)
          return newNavigator
        })
      }
    } else {
      setNavigator((previousNavigatorState) => {
        const newNavigator = previousNavigatorState.slice(0, -1)
        if (newNavigator.length === 0) setIsSidebarVisible(false)
        return newNavigator
      })
    }
  }

  const onSidebarPopToTop = () => {
    setNavigator((previousNavigatorState) => {
      previousNavigatorState.forEach((content, index) => index > 0 && void content.onHide?.())

      return previousNavigatorState[0] ? [previousNavigatorState[0]] : []
    })
  }

  const setCustomHeaderButtons = (buttons: React.ReactElement[]) => {
    setNavigator((previousNavigatorState) => {
      return previousNavigatorState.map((state, stateIndex) => {
        if (stateIndex === previousNavigatorState.length - 1)
          return {
            ...state,
            customHeaderButtons: buttons,
          }
        else return state
      })
    })
  }

  const setCurrentStackState = (key: string, value: string) => {
    setNavigator((previousNavigatorState) => {
      return previousNavigatorState.map((navState, navIndex) => {
        if (navIndex === previousNavigatorState.length - 1) {
          return {
            ...navState,
            state: {
              ...navState.state,
              [key]: value,
            },
          }
        } else {
          return navState
        }
      })
    })
  }

  const setCurrentStackOnHide = (onHide: () => Promise<boolean>) => {
    setNavigator((previousNavigatorState) => {
      return previousNavigatorState.map((navState, navIndex) => {
        if (navIndex === previousNavigatorState.length - 1) {
          return { ...navState, onHide }
        } else return navState
      })
    })
  }

  const getCurrentStackState = (key: string) => {
    return currentContent?.state?.[key]
  }

  const addSidebarStackOnTop = (arg: NavigatorStack) => {
    setSidebarStackOnTop(arg)
    setIsSidebarVisible(true)
  }

  const hideSidebarStackOnTop = () => {
    setSidebarStackOnTop(undefined)
    document.querySelector('[data-slot="right-sidebar-content"]')?.scrollTo({ top: 0 })
    if (navigator.length === 0) setIsSidebarVisible(false)
  }

  const sidebarStackOnTopLeftButtonClick = () => {
    if (sidebarStackOnTop?.sidebarLeftButtonClick) {
      void sidebarStackOnTop.sidebarLeftButtonClick()
    } else {
      hideSidebarStackOnTop()
    }
  }

  const updateSidebarStackOnTop = (arg: Partial<NavigatorStack>) => {
    if (sidebarStackOnTop) {
      setSidebarStackOnTop({
        ...sidebarStackOnTop,
        ...arg,
      })
    }
  }

  const contextValue = {
    hideSidebar: onHideSidebar,
    sidebarNavigateTo: onSidebarNavigate,
    sidebarNavigateBack: onSidebarNavigateBack,
    sidebarPopToTop: onSidebarPopToTop,
    setCustomHeaderButtons,
    setCurrentStackState,
    setCurrentStackOnHide,
    getCurrentStackState,
    isSidebarVisible,
    addSidebarStackOnTop,
    hideSidebarStackOnTop,
    updateSidebarStackOnTop,
  }

  const onLeftButtonClick = async () => {
    if (typeof currentContent?.sidebarLeftButtonClick !== "undefined") {
      const result = await currentContent.sidebarLeftButtonClick()
      if (result) await onSidebarNavigateBack()
    } else {
      await onSidebarNavigateBack()
    }
  }

  return (
    <HUSidebarContext.Provider value={contextValue}>
      <Sheet
        open={isSidebarVisible}
        onOpenChange={(open) => !open && !sidebarStackOnTop && void onHideSidebar.current()}
      >
        <SheetContent
          id="sidebar-context-content"
          data-slot="right-sidebar-context"
          className={cn(
            "h-svh w-full bg-surface-background",
            // Override the Sheet's data-[side] width specificity so the sidebar keeps its pre-refactor width
            "data-[side=left]:w-full data-[side=right]:w-full",
            "sm:data-[side=left]:w-xl sm:data-[side=left]:max-w-none sm:data-[side=right]:w-xl sm:data-[side=right]:max-w-none",
          )}
          style={isSidebarReduced ? { width: 70 } : undefined}
          side={sidebarPosition}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Sidebar Context</SheetTitle>
            <SheetDescription>Displays the sidebar.</SheetDescription>
          </SheetHeader>

          <Activity mode={sidebarStackOnTop ? "visible" : "hidden"}>
            {/* TODO: Remove overflow-x-scroll, program pack with datable is reponsive sidebar  */}
            <div
              className="relative flex h-full w-full flex-col overflow-x-scroll"
              data-slot="right-sidebar-content-stack-on-top"
            >
              <div className="absolute top-0 left-0 h-auto w-full pt-4">
                <div className={cn("flex w-full justify-between px-4")}>
                  {/* LEFT BUTTON */}
                  <Button
                    variant="outline"
                    size="iconMd"
                    onClick={() => {
                      sidebarStackOnTopLeftButtonClick()
                    }}
                    className="translate-2"
                  >
                    <ArrowLeft color="currentColor" size={16} />
                  </Button>

                  {sidebarStackOnTop?.customHeaderButtons?.map((button, index) => (
                    <span key={`customHeaderButton-${index.toString()}`}>{button}</span>
                  ))}
                </div>
              </div>
              <div className="flex flex-1 flex-col">{sidebarStackOnTop?.content}</div>
            </div>
          </Activity>

          <Activity mode={sidebarStackOnTop ? "hidden" : "visible"}>
            <Activity mode={isSidebarReduced ? "visible" : "hidden"}>
              <div className="flex w-full items-center">
                <div className="mx-4 mt-4 flex w-full justify-end">
                  <Button
                    variant="outline"
                    size="iconMd"
                    onClick={toggleSidebarReduced}
                    title={word("sidebar.button.expand")}
                  >
                    <FastForwardIcon color="currentColor" size={16} className="rotate-180" />
                  </Button>
                </div>
              </div>
            </Activity>

            {/* HEADER OF THE SIDEBAR */}
            <Activity mode={isSidebarReduced ? "hidden" : "visible"}>
              {/* TODO: Remove overflow-x-scroll, program pack with datable is reponsive sidebar  */}
              <div className="relative flex h-full w-full flex-col overflow-y-auto" data-slot="right-sidebar-content">
                <div className="absolute z-50 top-0 left-0 h-auto w-full pt-4">
                  <div className={cn("flex w-full px-4", navigator.length > 1 ? "justify-between" : "justify-end")}>
                    {/* LEFT BUTTON */}
                    {navigator.length > 1 && (
                      <Button
                        variant="outline"
                        size="iconMd"
                        onClick={() => void onLeftButtonClick()}
                        className="translate-2"
                      >
                        <ArrowLeft color="currentColor" size={16} />
                      </Button>
                    )}

                    {/* RIGHT BUTTON */}
                    <div className="flex flex-row gap-2">
                      {currentContent?.customHeaderButtons?.map((button, index) => (
                        <span key={`customHeaderButton-${index.toString()}`}>{button}</span>
                      ))}
                      {currentContent?.allowSidebarReduction && (
                        <Button
                          size="iconMd"
                          variant="outline"
                          onClick={toggleSidebarReduced}
                          title={word("sidebar.button.reduce")}
                        >
                          <FastForwardIcon color="currentColor" size={16} />
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="iconMd"
                        onClick={() => {
                          switchSidebarSide()
                        }}
                      >
                        <ArrowLeftRight color="currentColor" size={16} />
                      </Button>
                      <Button autoFocus variant="outline" size="iconMd" onClick={() => void onHideSidebar.current()}>
                        <X color="currentColor" size={16} />
                      </Button>
                    </div>
                  </div>
                </div>
                <div className="flex w-full grow flex-col">{currentContent?.content}</div>
              </div>
            </Activity>
          </Activity>
        </SheetContent>
      </Sheet>
      {children}
    </HUSidebarContext.Provider>
  )
}
